/**
 * Outil « Tableau de numération » — calculs (pur).
 *
 * Un nombre décimal (au plus 5 chiffres avant la virgule, 3 après) est rangé
 * dans un tableau de numération des centaines de mille aux dix-millièmes.
 * Multiplier ou diviser par 10, 100, 1 000 décale chaque chiffre d'autant de
 * rangs : on garde l'exposant d'ORIGINE de chaque chiffre (sa clé) et le
 * décalage total, pour que l'affichage fasse glisser les mêmes chiffres.
 */

export const MAX_EXP = 5;
export const MIN_EXP = -4;

/** colonnes, de gauche à droite (exposant de 10) */
/** (partie entière en majuscules, partie décimale en minuscules, comme au tableau) */
export const COLUMNS: ReadonlyArray<{ exp: number; short: string; name: string }> = [
  { exp: 5, short: 'CM', name: 'centaines de mille' },
  { exp: 4, short: 'DM', name: 'dizaines de mille' },
  { exp: 3, short: 'UM', name: 'unités de mille' },
  { exp: 2, short: 'C', name: 'centaines' },
  { exp: 1, short: 'D', name: 'dizaines' },
  { exp: 0, short: 'U', name: 'unités' },
  { exp: -1, short: 'd', name: 'dixièmes' },
  { exp: -2, short: 'c', name: 'centièmes' },
  { exp: -3, short: 'm', name: 'millièmes' },
  { exp: -4, short: 'dm', name: 'dix-millièmes' },
];

export interface Digit {
  /** exposant d'origine (clé stable pour l'animation) */
  key: number;
  digit: number;
}

/** Chiffres non nuls d'un nombre (au plus 4 décimales), avec leur exposant. */
export function digitsOf(value: number): Digit[] {
  const units = Math.round(value * 10 ** -MIN_EXP);
  const out: Digit[] = [];
  let u = units;
  let e = MIN_EXP;
  while (u > 0) {
    const d = u % 10;
    if (d) out.push({ key: e, digit: d });
    u = Math.floor(u / 10);
    e++;
  }
  return out;
}

/** Le décalage reste-t-il dans le tableau ? */
export function canShift(digits: readonly Digit[], shift: number): boolean {
  return digits.every((d) => d.key + shift <= MAX_EXP && d.key + shift >= MIN_EXP);
}

/** Valeur exacte après décalage, écrite à la française (« 34,7 », « 0,0347 »). */
export function shiftedText(digits: readonly Digit[], shift: number): string {
  if (digits.length === 0) return '0';
  const exps = digits.map((d) => d.key + shift);
  const hi = Math.max(0, ...exps);
  const lo = Math.min(0, ...exps);
  let s = '';
  for (let e = hi; e >= lo; e--) {
    if (e === -1) s += ',';
    s += digits.find((d) => d.key + shift === e)?.digit ?? 0;
  }
  return s.replace(/^0+(?=\d)/, '');
}

/** Cases à afficher : chiffres placés + zéros utiles (entre le premier chiffre et l'unité). */
export function displayedCells(
  digits: readonly Digit[],
  shift: number,
): Array<{ exp: number; digit: number; key: string }> {
  if (digits.length === 0) return [{ exp: 0, digit: 0, key: 'z0' }];
  const exps = digits.map((d) => d.key + shift);
  const hi = Math.max(0, ...exps);
  const lo = Math.min(0, ...exps);
  const out: Array<{ exp: number; digit: number; key: string }> = [];
  for (let e = hi; e >= lo; e--) {
    const d = digits.find((x) => x.key + shift === e);
    out.push(d ? { exp: e, digit: d.digit, key: `d${d.key}` } : { exp: e, digit: 0, key: `z${e}` });
  }
  return out;
}

/**
 * Espace fine insécable entre les classes de la PARTIE ENTIÈRE seulement :
 * « 12345,6789 » → « 12 345,6789 » (les décimales ne se groupent pas ici).
 */
export function groupThousands(text: string): string {
  const [int, dec] = text.split(',');
  const g = int!.replace(/\B(?=(\d{3})+$)/g, '\u202f');
  return dec === undefined ? g : `${g},${dec}`;
}

/** « 34,7 = 3 × 10 + 4 × 1 + 7 × 0,1 » (chiffres non nuls) */
export function decomposition(digits: readonly Digit[], shift: number): string {
  const unit = (e: number) =>
    e >= 0 ? groupThousands(String(10 ** e)) : `0,${'0'.repeat(-e - 1)}1`;
  const parts = [...digits]
    .sort((a, b) => b.key - a.key)
    .map((d) => `${d.digit} × ${unit(d.key + shift)}`);
  return parts.length ? parts.join(' + ') : '0';
}

/** Lecture des chiffres pour un lecteur d'écran : « 3 unités, 4 dixièmes, 7 centièmes ». */
export function describeDigits(digits: readonly Digit[], shift: number): string {
  if (digits.length === 0) return 'zéro';
  return [...digits]
    .sort((a, b) => b.key - a.key)
    .map((d) => {
      const name = COLUMNS.find((c) => c.exp === d.key + shift)?.name ?? '';
      return `${d.digit} ${d.digit === 1 ? name.replace(/s( de mille)?$/, '$1') : name}`;
    })
    .join(', ');
}

export const OPERATIONS: ReadonlyArray<{ label: string; shift: number }> = [
  { label: '× 10', shift: 1 },
  { label: '× 100', shift: 2 },
  { label: '× 1 000', shift: 3 },
  { label: '÷ 10', shift: -1 },
  { label: '÷ 100', shift: -2 },
  { label: '÷ 1 000', shift: -3 },
];
